import { Breadcrumbs, TextFading } from '@yes.technology/react-toolkit'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { omitKeys } from 'shared/utils/types/omitKeys'
import {
  CANCEL_SELECT_AND_LINK_NESTED_POST_MESSAGE_TYPE,
  CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE,
  DEFAULT_FILTER_FIELDS_SESSION_ID,
  MODAL_DEFAULT_ZINDEX,
  OPEN_SELECT_AND_LINK_POST_MESSAGE_TYPE,
  SELECT_AND_LINK_NESTED_RESULT_POST_MESSAGE_TYPE,
  SELECT_AND_LINK_PARAM,
  SELECT_AND_LINK_POST_MESSAGE_TYPE,
  targetComponents
} from './SelectAndLink.constants'
import {
  FullSelectionOption,
  SelectAndLinkContextValue,
  SelectAndLinkSession,
  SelectAndLinkSessionConfig,
  SelectAndLinkValue
} from './SelectAndLink.types'
import SelectAndLinkCard from './SelectAndLinkCard'
import { isMinimalSelectionOption } from './utils/isMinimalSelectionOption'

export const SelectAndLinkContext =
  React.createContext<SelectAndLinkContextValue | null>(null)

type SelectAndLinkProviderProps = {
  children: React.ReactNode
}

const emptySession: SelectAndLinkSession = {
  onSelect: () => undefined,
  targetField: '',
  targetGroup: '',
  objectclassUuid: '',
  objectClassTitle: '',
  targetComponent: targetComponents.FILTER_FIELDS,
  selectedOptions: [],
  isCompleted: false
}

export function SelectAndLinkProvider({
  children
}: SelectAndLinkProviderProps) {
  const [searchParams] = useSearchParams()
  const isInIframeMode = searchParams.has(SELECT_AND_LINK_PARAM)

  const [sessions, setSessions] = useState<
    Record<string, SelectAndLinkSession>
  >({})
  const [modalStack, setModalStack] = useState<string[]>([])

  const modalStackRef = useRef(modalStack)
  const sessionsRef = useRef(sessions)

  useEffect(() => {
    modalStackRef.current = modalStack
  }, [modalStack])

  useEffect(() => {
    sessionsRef.current = sessions
  }, [sessions])

  const onSelectForId = useCallback(
    (sessionId: string, values: SelectAndLinkValue[]) => {
      setSessions((prev): Record<string, SelectAndLinkSession> => {
        const current = prev[sessionId]
        if (!current) return prev

        const urlOptions: FullSelectionOption[] =
          current.urlSelectedOptions ?? []

        const merged: SelectAndLinkSession['selectedOptions'] = values.flatMap(
          (v): SelectAndLinkSession['selectedOptions'] => {
            if (isMinimalSelectionOption(v)) {
              const full = urlOptions.find((o) => o.uuid === v.uuid)
              return full ? [full] : []
            }
            return [v]
          }
        )

        return {
          ...prev,
          [sessionId]: {
            ...current,
            isCompleted: true,
            selectedOptions: merged
          }
        }
      })
    },
    []
  )

  const onClearSelection = useCallback((sessionId?: string) => {
    if (sessionId) {
      setSessions((prev) => omitKeys(prev, sessionId))
      setModalStack((prev) => prev.filter((id) => id !== sessionId))
    } else {
      const topId = modalStackRef.current[modalStackRef.current.length - 1]
      if (topId) {
        setSessions((prev) => omitKeys(prev, topId))
      } else {
        setSessions({})
      }
      setModalStack((prev) => prev.slice(0, -1))
    }
  }, [])

  const openSelectAndLink = useCallback(
    (config: SelectAndLinkSessionConfig, sessionId?: string) => {
      const id =
        sessionId ??
        (config.targetComponent === targetComponents.FILTER_FIELDS
          ? DEFAULT_FILTER_FIELDS_SESSION_ID
          : (config.targetField ?? `session_${Date.now()}`))

      const sessionOnSelect = (values: SelectAndLinkValue[]) => {
        onSelectForId(id, values)
      }

      setSessions((prev) => {
        const previous = prev[id]
        const shouldPreserveExisting =
          !!sessionId && (previous?.selectedOptions?.length ?? 0) > 0
        const mergedSelected = shouldPreserveExisting
          ? previous.selectedOptions
          : (config.selectedOptions ?? [])

        const newSession: SelectAndLinkSession = {
          ...emptySession,
          ...config,
          objectClassTitle: config.objectClassTitle ?? '',
          urlSelectedOptions: config.selectedOptions ?? [],
          selectedOptions: mergedSelected,
          isCompleted: shouldPreserveExisting
            ? Boolean(previous?.isCompleted)
            : false,
          onSelect: sessionOnSelect
        }

        return { ...prev, [id]: newSession }
      })

      if (isInIframeMode) {
        window.parent.postMessage(
          {
            type: OPEN_SELECT_AND_LINK_POST_MESSAGE_TYPE,
            config,
            sessionId: id
          },
          window.location.origin
        )
      } else {
        setModalStack((prev) => (prev.includes(id) ? prev : [...prev, id]))
      }
    },
    [onSelectForId, isInIframeMode]
  )

  const closeSelectAndLink = useCallback(() => {
    const currentStack = modalStackRef.current
    const topId = currentStack[currentStack.length - 1]
    if (!topId) return

    const topSession = sessionsRef.current[topId]

    if (topSession?.nestedOrigin) {
      topSession.nestedOrigin.source.postMessage(
        {
          type: CANCEL_SELECT_AND_LINK_NESTED_POST_MESSAGE_TYPE,
          sessionId: topSession.nestedOrigin.sessionId
        },
        window.location.origin
      )
      setSessions((prev) => omitKeys(prev, topId))
    }

    setModalStack((prev) => prev.slice(0, -1))
  }, [])

  const getSession = useCallback(
    (sessionId: string): SelectAndLinkSession | undefined =>
      sessions[sessionId],
    [sessions]
  )

  const removeSelectedOptionByUuid = useCallback(
    (uuid: string, sessionId?: string) => {
      const currentStack = modalStackRef.current
      const id = sessionId ?? currentStack[currentStack.length - 1]
      if (!id) return
      setSessions((prev) => {
        const current = prev[id]
        if (!current) return prev
        const filtered = current.selectedOptions.filter((o) => o.uuid !== uuid)
        return {
          ...prev,
          [id]: { ...current, selectedOptions: filtered }
        }
      })
    },
    []
  )

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      const type = event.data?.type
      const currentStack = modalStackRef.current
      const currentSessions = sessionsRef.current

      if (type === SELECT_AND_LINK_POST_MESSAGE_TYPE) {
        const topId = currentStack[currentStack.length - 1]
        if (!topId) return

        const topSession = currentSessions[topId]
        if (!topSession) return

        topSession.onSelect(event.data.values ?? [])

        if (topSession.nestedOrigin) {
          setSessions((prev) => omitKeys(prev, topId))
        }
        setModalStack((prev) => prev.slice(0, -1))
        return
      }

      if (type === CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE) {
        const topId = currentStack[currentStack.length - 1]
        if (!topId) return

        const topSession = currentSessions[topId]
        if (topSession?.nestedOrigin) {
          topSession.nestedOrigin.source.postMessage(
            {
              type: CANCEL_SELECT_AND_LINK_NESTED_POST_MESSAGE_TYPE,
              sessionId: topSession.nestedOrigin.sessionId
            },
            window.location.origin
          )
        }

        setSessions((prev) => omitKeys(prev, topId))
        setModalStack((prev) => prev.slice(0, -1))
        return
      }

      if (type === OPEN_SELECT_AND_LINK_POST_MESSAGE_TYPE) {
        const { config, sessionId } = event.data as {
          config: SelectAndLinkSessionConfig
          sessionId: string
        }
        const requestingWindow = event.source as Window
        const nestedId = `nested_${sessionId}_${Date.now()}`

        const nestedOnSelect = (values: SelectAndLinkValue[]) => {
          requestingWindow.postMessage(
            {
              type: SELECT_AND_LINK_NESTED_RESULT_POST_MESSAGE_TYPE,
              sessionId,
              values
            },
            window.location.origin
          )
        }

        const nestedSession: SelectAndLinkSession = {
          ...emptySession,
          ...config,
          objectClassTitle: config.objectClassTitle ?? '',
          urlSelectedOptions: config.selectedOptions ?? [],
          selectedOptions: config.selectedOptions ?? [],
          isCompleted: false,
          onSelect: nestedOnSelect,
          nestedOrigin: { source: requestingWindow, sessionId }
        }

        setSessions((prev) => ({ ...prev, [nestedId]: nestedSession }))
        setModalStack((prev) => [...prev, nestedId])
        return
      }

      if (type === SELECT_AND_LINK_NESTED_RESULT_POST_MESSAGE_TYPE) {
        const { sessionId, values } = event.data as {
          sessionId: string
          values: SelectAndLinkValue[]
        }
        onSelectForId(sessionId, values)
        return
      }

      if (type === CANCEL_SELECT_AND_LINK_NESTED_POST_MESSAGE_TYPE) {
        const { sessionId } = event.data as { sessionId: string }
        onClearSelection(sessionId)
        return
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onSelectForId, onClearSelection])

  useEffect(() => {
    if (modalStack.length === 0) return
    const previousOverflow = document.body.style.overflow
    const scrollY = window.scrollY
    document.body.style.overflow = 'hidden'
    window.scrollTo(0, scrollY)
    return () => {
      document.body.style.overflow = previousOverflow
      window.scrollTo(0, scrollY)
    }
  }, [modalStack.length])

  const isOpen = modalStack.length > 0
  const activeSessionId = modalStack[modalStack.length - 1] ?? null

  const hasPersistentSelection =
    isOpen || Object.keys(sessions).some((id) => sessions[id]?.isCompleted)

  const value: SelectAndLinkContextValue = useMemo(
    () => ({
      openSelectAndLink,
      closeSelectAndLink,
      onClearSelection,
      isOpen,
      sessions,
      activeSessionId,
      modalStack,
      getSession,
      hasPersistentSelection,
      removeSelectedOptionByUuid
    }),
    [
      openSelectAndLink,
      closeSelectAndLink,
      onClearSelection,
      isOpen,
      sessions,
      activeSessionId,
      modalStack,
      getSession,
      hasPersistentSelection,
      removeSelectedOptionByUuid
    ]
  )

  return (
    <SelectAndLinkContext.Provider value={value}>
      {children}
      {modalStack.map((id, index) => {
        const session = sessions[id]
        if (!session) return null
        const breadcrumbRoutes = [
          ...[{ description: document.title ?? '', url: '' }],
          ...modalStack.slice(0, index + 1).map((sessionId) => ({
            description: sessions[sessionId]?.objectClassTitle ?? '',
            url: ''
          }))
        ]
        return (
          <SelectAndLinkCard
            key={id}
            title={
              <TextFading>
                <Breadcrumbs
                  color='black'
                  boldLastItem={false}
                  spaceBetween='8px'
                  fontStyle='italic'
                  heightClass='large'
                  separator='|'
                  routes={breadcrumbRoutes}
                />
              </TextFading>
            }
            session={session}
            zIndex={MODAL_DEFAULT_ZINDEX + index}
            onClose={closeSelectAndLink}
          />
        )
      })}
    </SelectAndLinkContext.Provider>
  )
}
